import { useRef, useMemo, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { Optional, ResumeWorkExperience } from '@rio/types'
import { matchPath, useLocation } from 'react-router-dom'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, selectors, SectionNames } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { RichTextEditorInstanceWrapper } from 'builder/components/RichTextArea/types'
import {
  PhraseData,
  PhrasesSuggestion,
  SuggestionsToggler,
  SUGGESTIONS_CHECK_KEY,
  ToggleSuggestionsParams,
} from 'builder/components/PhrasesSuggestion'
import { useKeywords } from 'builder/components/AiFeatures/Keywords'
import { SuggestionVariantsPopup } from 'builder/components/AiFeatures/SuggestionVariantsPopup'
import { useConfig } from 'builder/hooks/useConfig'
import {
  AiWorkExperience,
  AiWorkExperienceResult,
  GenerateWithTailoredRole,
  ANALYTICS_SECTION_NAME,
} from 'builder/components/AiFeatures/AiWorkExperience'
import { AiAssistantProcessing } from 'builder/components/AiFeatures/AiProfileSummary'
import { GenerateAiSummaryStatus } from 'builder/modules/constants'
import { isTruthy } from 'builder/utils/richTextUtils'

type Props = {
  editorInstanceRef: React.RefObject<RichTextEditorInstanceWrapper>
  editorContainerRef: React.RefObject<HTMLDivElement>
  jobTitle: Optional<string>
  currentWorkExperience: ResumeWorkExperience
}

function shouldUseAiPhrases(locale: string) {
  return ['en', 'en-GB', 'nl-NL'].includes(locale)
}

// for some reason this string was used for a long time instead of SectionNames.workExperiences
// and already registered in analytic events, so we can't change it
const customSectionName = 'workExperience'

export const useWorkExperienceSuggestions = (props: Props) => {
  const { editorInstanceRef, editorContainerRef, jobTitle, currentWorkExperience } = props
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const suggestionLocales = useTypedSelector(state => state.generalEditor.suggestionLocales)
  const {
    isModalOpen: isAiWorkExperienceModalOpen,
    isResultModalOpen: isAiWorkExperienceResultModalOpen,
    summary: generatedContent,
    isWithTailoredResumeModalOpen,
    generateAiSummaryStatus,
    shouldAnimateEditorFieldContent,
  } = useTypedSelector(selectors.aiWorkExperience)
  const resume = useTypedSelector(selectors.resume)
  const { locale = '' } = resume || {}
  const phrasesOpenerRef = useRef<HTMLDivElement>(null)
  const phrasesSuggestionRef = useRef<HTMLDivElement>(null)
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false)
  const isAiAction = shouldUseAiPhrases(locale)
  const suggestionEnabled = locale && suggestionLocales.workExperience?.includes(locale)
  const { isKeywordsAvailable } = useKeywords()
  const [isSuggestionVariantsOpen, setIsSuggestionVariantsOpen] = useState(false)
  const { pathname } = useLocation()
  const isCoverLetterEditor = matchPath('cover-letters/:id/edit', pathname)
  const isTailorOverviewEditor = resume && resume.autoTailored && resume.draft
  const config = useConfig()
  const isAiWorkExperienceEnabled = !!config?.features.aiWorkExp
  const isDescriptionEmpty = !isTruthy(currentWorkExperience.description)
  const isProcessingModalOpen = [
    GenerateAiSummaryStatus.failed,
    GenerateAiSummaryStatus.processing,
  ].includes(generateAiSummaryStatus)
  const isMobileModalScrollDisabled = isProcessingModalOpen || isWithTailoredResumeModalOpen

  const onToggleSuggestions = useCallback(
    (state: ToggleSuggestionsParams) => {
      const { isOpen, trigger } = state
      const newState = isOpen || !isSuggestionsOpen

      setIsSuggestionsOpen(newState)

      if (newState) {
        dispatch(actions.setIsAiProfileSummaryModalOpen({ status: false }))
        dispatch(actions.setIsAiWorkExperienceModalOpen(false))
        trackInternalEvent('open_pre_written_phrases_dialog', {
          section: ANALYTICS_SECTION_NAME,
          trigger,
        })
      }
    },
    [dispatch, isSuggestionsOpen],
  )

  const onFocus = useCallback(() => {
    if (isCoverLetterEditor || isTailorOverviewEditor) return

    const isTunerTipsEnabled = window.location.search.includes('tuner_tips')
    const storedSuggestions = localStorage.getItem(SUGGESTIONS_CHECK_KEY)
    // Additional check to avoid JSON parsing error
    const parsedSuggestionsState = storedSuggestions ? JSON.parse(storedSuggestions) : null
    const suggestionsState = Array.isArray(parsedSuggestionsState) ? parsedSuggestionsState : []
    const suggestionsWereShown = suggestionsState.includes(customSectionName)

    if (!suggestionsWereShown && !isTunerTipsEnabled) {
      if (isAiWorkExperienceEnabled) {
        dispatch(actions.setIsAiWorkExperienceModalOpen(true))
      } else {
        onToggleSuggestions({ isOpen: true, trigger: 'focus_field' })
      }
      suggestionsState.push(customSectionName)
      const updatedSuggestionState = suggestionsState.filter(Boolean)
      if (updatedSuggestionState.length > 0) {
        localStorage.setItem(SUGGESTIONS_CHECK_KEY, JSON.stringify(updatedSuggestionState))
      }
    }
  }, [
    isCoverLetterEditor,
    isTailorOverviewEditor,
    isAiWorkExperienceEnabled,
    dispatch,
    onToggleSuggestions,
  ])

  const additionalToolbarActions = useMemo(() => {
    if (!suggestionEnabled) {
      return null
    }

    const toggleSuggestionVariants = (newState?: { isOpen?: boolean }) => {
      setIsSuggestionVariantsOpen(state => {
        return newState && 'isOpen' in newState ? !!newState.isOpen : !state
      })
    }

    const onAddPhrase = (phrase: PhraseData) => {
      editorInstanceRef.current?.addPhrase(phrase, false)
    }

    const onToggleSuggestionVariants = () => {
      if (!isSuggestionVariantsOpen) {
        trackInternalEvent('click_writing_help', {
          section: ANALYTICS_SECTION_NAME,
          description_empty: isDescriptionEmpty,
        })
      }
      toggleSuggestionVariants()
    }

    const onToggleAiWorkExperienceModal = () => {
      if (
        !isSuggestionsOpen &&
        !isAiWorkExperienceResultModalOpen &&
        !isAiWorkExperienceModalOpen
      ) {
        trackInternalEvent('click_writing_help', {
          section: ANALYTICS_SECTION_NAME,
          description_empty: isDescriptionEmpty,
        })
      }

      if (isSuggestionsOpen) {
        setIsSuggestionsOpen(false)
        return
      }

      if (generatedContent) {
        dispatch(actions.setIsAiWorkExperienceResultOpen(true))
        dispatch(actions.setAIProfileResultContentAnimation(false))
      } else {
        dispatch(actions.setIsAiWorkExperienceModalOpen(!isAiWorkExperienceModalOpen))
      }
    }

    return (
      <>
        {isAiWorkExperienceEnabled ? (
          <SuggestionsToggler
            isActive={isAiWorkExperienceModalOpen}
            ref={phrasesOpenerRef}
            onToggle={onToggleAiWorkExperienceModal}
            text={i18n.t('builder.ai_profile_summary.help_with_writing_modal.button_text')}
            isAiAction={true}
          />
        ) : isKeywordsAvailable ? (
          <SuggestionsToggler
            isActive={isSuggestionVariantsOpen}
            ref={phrasesOpenerRef}
            onToggle={onToggleSuggestionVariants}
            text={i18n.t('builder.ai_profile_summary.help_with_writing_modal.button_text')}
            isAiAction={true}
          />
        ) : (
          <SuggestionsToggler
            isActive={isSuggestionsOpen}
            ref={phrasesOpenerRef}
            onToggle={() => onToggleSuggestions({ trigger: 'click' })}
            {...{ isAiAction }}
          />
        )}
        {isSuggestionVariantsOpen && (
          <SuggestionVariantsPopup
            onClose={() => setIsSuggestionVariantsOpen(false)}
            onAddPrewrittenPhrasesClick={() =>
              onToggleSuggestions({ isOpen: true, trigger: 'click' })
            }
            keywordsConfig={{
              section: SectionNames.workExperiences,
              buttonText: i18n.t('builder.resume_editor.work_experiences_card.add_keywords'),
            }}
            {...{ phrasesOpenerRef, editorContainerRef }}
          />
        )}
        {isSuggestionsOpen && (
          <PhrasesSuggestion
            text={editorInstanceRef.current?.getEditorText() || ''}
            onSelect={onAddPhrase}
            locale={locale}
            outsideOpenerRef={phrasesOpenerRef}
            scope={customSectionName}
            elementRef={phrasesSuggestionRef}
            editorContainerRef={editorContainerRef}
            onToggleSuggestions={onToggleSuggestions}
            jobTitle={jobTitle || ''}
          />
        )}
        {isAiWorkExperienceModalOpen && !generatedContent && (
          <AiWorkExperience
            {...{
              phrasesOpenerRef,
              editorContainerRef,
              onToggleSuggestions,
              currentWorkExperience,
            }}
          />
        )}
        {isAiWorkExperienceResultModalOpen && (
          <AiWorkExperienceResult
            {...{
              phrasesOpenerRef,
              editorContainerRef,
              currentWorkExperience,
            }}
          />
        )}
        {isProcessingModalOpen && (
          <AiAssistantProcessing
            sectionNameForAnalytics={ANALYTICS_SECTION_NAME}
            status={generateAiSummaryStatus}
            onBackClick={() => {
              dispatch(actions.resetGenerateAiWorkExperienceStatus())
              dispatch(actions.setIsAiWorkExperienceModalOpen(true))
            }}
            onTryAgainClick={() => {
              dispatch(
                actions.generateResumeAiWorkExperience({
                  workExperienceId: currentWorkExperience.id,
                }),
              )
            }}
          />
        )}
        {isWithTailoredResumeModalOpen && (
          <GenerateWithTailoredRole currentWorkExperience={currentWorkExperience} />
        )}
      </>
    )
  }, [
    suggestionEnabled,
    isAiWorkExperienceEnabled,
    isAiWorkExperienceModalOpen,
    i18n,
    isKeywordsAvailable,
    isSuggestionVariantsOpen,
    isSuggestionsOpen,
    isAiAction,
    editorContainerRef,
    editorInstanceRef,
    locale,
    onToggleSuggestions,
    jobTitle,
    generatedContent,
    currentWorkExperience,
    isAiWorkExperienceResultModalOpen,
    generateAiSummaryStatus,
    isWithTailoredResumeModalOpen,
    isDescriptionEmpty,
    dispatch,
    isProcessingModalOpen,
  ])

  return {
    additionalToolbarActions,
    onFocus,
    shouldAnimateEditorFieldContent,
    isMobileModalScrollDisabled,
  }
}
